import dayjs from "dayjs";
import { toRomaji } from "wanakana";
import { useLayoutEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { menu } from "../../../constants";
import { formatGender, calculateAge } from "../../../utils/webCvUtils";
import sty from "./EngineerList.module.css";
import Swal from "sweetalert2";
import { errorMessages } from "../../../utils/messagesUtils";
import SkillTagFilter from "../../../components/input/SkillTagFilter/SkillTagFilter";
import { useSkillTags } from "../../../hooks/useSkillTags";

const EngineerList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [appliedTags, setAppliedTags] = useState([]);
  const [tagCategories, setTagCategories] = useState({});

  const [showMemoModal, setShowMemoModal] = useState(false);
  const [currentMemo, setCurrentMemo] = useState({
    name: "",
    memo: "",
  });

  const { transformSkillTagsData, filterEngineers } = useSkillTags({
    selectedTags,
    setSelectedTags,
    tagCategories,
    mode: "filter",
  });

  useLayoutEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/share/engineers`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
          },
        );
        const result = await response.json();
        if (response.status === 200 && result.status === "OK") {
          setData(result.data);
        } else {
          if (response.status === 401) {
            navigate(menu.ENGINEER.SIGNIN.PATH);
          } else {
            const error = new Error();
            error.front_message = errorMessages.common.data_fetch_failed;
            throw error;
          }
        }
      } catch (e) {
        setData([]);
        await Swal.fire({
          icon: "error",
          html: `${e.front_message || errorMessages.api.server_error}`,
          customClass: {
            htmlContainer: "large-text",
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSkillTagsData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/skill-tags?userType=share`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
          },
        );
        const result = await response.json();
        if (response.status === 200 && result.status === "OK") {
          setTagCategories(transformSkillTagsData(result.data));
        } else {
          const error = new Error();
          error.front_message = errorMessages.common.data_fetch_failed;
          throw error;
        }
      } catch (e) {
        await Swal.fire({
          icon: "error",
          html: `${e.front_message || errorMessages.api.server_error}`,
          customClass: {
            htmlContainer: "large-text",
          },
        });
      }
    };
    fetchData();
    fetchSkillTagsData();
  }, [navigate]);

  // 検索実行ハンドラー
  const handleSearch = (tagsToApply = null) => {
    const tagsToUse = tagsToApply !== null ? tagsToApply : selectedTags;
    setAppliedTags([...tagsToUse]);
  };

  // データフィルタリング
  const filteredData = filterEngineers(data, appliedTags, "profile.skillTags");

  // 全ての日付を比較して最新の更新日を返す
  const getLatestUpdateDate = (engineer) => {
    const dates = [
      engineer?.profile?.updatedAt,
      engineer?.certifications?.updatedAt,
      engineer?.projects?.updatedAt,
      engineer?.works?.updatedAt,
    ].filter((date) => date); // null や undefined を除外

    if (dates.length === 0) return null;

    return dates.reduce((latest, current) => {
      return dayjs(current).isAfter(dayjs(latest)) ? current : latest;
    });
  };

  // 名前表示の関数
  const getDisplayName = (engineer) => {
    const lastName = engineer?.profile?.last_name;
    const firstName = engineer?.profile?.first_name;

    if (!lastName || !firstName) {
      return "未登録";
    }
    // イニシャル表示
    const lastNameRomaji = toRomaji(engineer?.profile?.last_name_kana);
    const lastNameInitial = lastNameRomaji.charAt(0).toUpperCase();
    const firstNameRomaji = toRomaji(engineer?.profile?.first_name_kana);
    const firstNameInitial = firstNameRomaji.charAt(0).toUpperCase();
    return `${firstNameInitial}.${lastNameInitial}`;
  };

  // 営業メモ表示ハンドラー
  const handleShowMemo = (engineer) => {
    const memoContent = engineer?.sales_memo?.content || "メモがありません。";

    setCurrentMemo({
      name: getDisplayName(engineer),
      memo: memoContent,
    });
    setShowMemoModal(true);
  };

  const handleCloseMemo = () => {
    setShowMemoModal(false);
  };

  return (
    <>
      <div
        className={`bg ${sty["bg--engineerlist-sp"]} ${showMemoModal ? sty["memo-open"] : ""}`}
      >
        <div className="page-tit">
          <div className="page-tit-outer">
            <p className="page-tit-txt">BLOWISH</p>
            <p className="page-tit-txt">エンジニア一覧</p>
            <div className="page-tit-border"></div>
          </div>
        </div>

        <SkillTagFilter
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          tagCategories={tagCategories}
          onSearch={handleSearch}
        />

        <div className={sty["list-pc"]}>
          <div className={sty["list-pc__table"]}>
            <div className={sty["list-pc__tr-h"]}>
              <div className={sty["list-pc__th"]}>氏名</div>
              <div className={sty["list-pc__th"]}>職種分類</div>
              <div className={sty["list-pc__th"]}>性別</div>
              <div className={sty["list-pc__th"]}>年齢</div>
              <div className={sty["list-pc__th"]}>スキルタグ</div>
              <div className={sty["list-pc__th"]}>更新日時</div>
              <div className={sty["list-pc__th"]}>営業メモ</div>
            </div>

            {isLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  fontSize: "1.2em",
                }}
              >
                Loading...
              </div>
            ) : filteredData.length > 0 ? (
              filteredData.map((engineer, index) => {
                {
                  /* ミニマムリリースでは、スキルシートは1つしか登録しないため、スキルシート1つ目のランダムIDを取得する */
                }
                const hasSkillsheet =
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random1Id &&
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random2Id;
                const linkPath =
                  hasSkillsheet &&
                  `/share/${engineer.skillsheetBox[0].skillsheet.output.random1Id}/${engineer.skillsheetBox[0].skillsheet.output.random2Id}`;

                return (
                  <div
                    className={sty["list-pc__tr-d"]}
                    key={engineer?.email || `engineer-pc-${index}`}
                  >
                    <div className={sty["list-pc__td"]}>
                      {getDisplayName(engineer)}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      <div>{engineer?.profile?.job_title || "未登録"}</div>
                      {engineer?.profile?.job_title2 && (
                        <div>{engineer?.profile?.job_title2}</div>
                      )}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      {formatGender(engineer?.profile?.gender)}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      {calculateAge(
                        engineer?.profile?.birth_year,
                        engineer?.profile?.birth_month,
                        engineer?.profile?.birth_day,
                      )}
                    </div>
                    <div
                      className={`${sty["list-pc__td"]} ${sty["skill-tag-container"]}`}
                    >
                      {(engineer?.profile?.skillTags || []).length > 0
                        ? (engineer?.profile?.skillTags || []).map(
                            (skill, index) => (
                              <div key={index} className={sty["skill-tag"]}>
                                {skill}
                              </div>
                            ),
                          )
                        : "未登録"}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      {getLatestUpdateDate(engineer)
                        ? dayjs(getLatestUpdateDate(engineer)).format(
                            "YYYY/MM/DD HH:mm",
                          )
                        : "未登録"}
                    </div>
                    <div className={sty["list-pc__td"]}>
                      <img
                        src="/images/description.svg"
                        alt="営業メモ"
                        onClick={() => handleShowMemo(engineer)}
                        className={sty["list-icon"]}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    {/* スキルシートが存在する場合のみリンクを表示 */}
                    {hasSkillsheet && (
                      <Link
                        to={linkPath}
                        target="_blank"
                        rel="noreferrer noopener"
                      ></Link>
                    )}
                  </div>
                );
              })
            ) : (
              <div className={sty["list__no-engineers"]}>
                {appliedTags.length > 0
                  ? "検索条件に一致するエンジニアが見つかりません"
                  : "エンジニアが登録されていません"}
              </div>
            )}
          </div>
        </div>

        <div className={sty["list-sp"]}>
          <div className={sty["list-sp__cards"]}>
            {isLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  fontSize: "1.2em",
                }}
              >
                Loading...
              </div>
            ) : filteredData.length > 0 ? (
              filteredData.map((engineer, index) => {
                {
                  /* ミニマムリリースでは、スキルシートは1つしか登録しないため、スキルシート1つ目のランダムIDを取得する */
                }
                const hasSkillsheet =
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random1Id &&
                  engineer?.skillsheetBox?.[0]?.skillsheet?.output?.random2Id;
                const linkPath =
                  hasSkillsheet &&
                  `/share/${engineer.skillsheetBox[0].skillsheet.output.random1Id}/${engineer.skillsheetBox[0].skillsheet.output.random2Id}`;

                return (
                  <div
                    key={engineer?.email || `engineer-sp-${index}`}
                    className={sty["list-sp__card"]}
                  >
                    <div className={sty["list-sp__card-header"]}>
                      <div className={sty["list-sp__profile"]}>
                        <div className={`${sty["list-sp__name"]}`}>
                          {getDisplayName(engineer)}
                        </div>
                        {formatGender(engineer?.profile?.gender)}/
                        {calculateAge(
                          engineer?.profile?.birth_year,
                          engineer?.profile?.birth_month,
                          engineer?.profile?.birth_day,
                        )}
                      </div>
                      <img
                        src="/images/description.svg"
                        alt="営業メモ"
                        onClick={() => handleShowMemo(engineer)}
                        className={sty["list-icon"]}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    <div className={sty["list-sp__card-content"]}>
                      <div className={`${sty["list-sp__job-title"]}`}>
                        <div>{engineer?.profile?.job_title || "未登録"}</div>
                        {engineer?.profile?.job_title2 && (
                          <div>{engineer?.profile?.job_title2}</div>
                        )}
                      </div>
                      <div className={`${sty["list-sp__skill-tags"]}`}>
                        {(engineer?.profile?.skillTags || []).length > 0
                          ? (engineer?.profile?.skillTags || []).map(
                              (skill, index) => (
                                <div key={index} className={sty["skill-tag"]}>
                                  {skill}
                                </div>
                              ),
                            )
                          : "未登録"}
                      </div>
                      <div className={`${sty["list-sp__updated-date"]}`}>
                        <img
                          src="/images/update.svg"
                          alt="更新日時"
                          className={sty["list-icon"]}
                        />
                        {getLatestUpdateDate(engineer)
                          ? dayjs(getLatestUpdateDate(engineer)).format(
                              "YYYY/MM/DD HH:mm",
                            )
                          : "未登録"}
                      </div>
                    </div>
                    {/* スキルシートが存在する場合のみリンクを表示 */}
                    {hasSkillsheet && (
                      <Link
                        to={linkPath}
                        target="_blank"
                        rel="noreferrer noopener"
                      ></Link>
                    )}
                  </div>
                );
              })
            ) : (
              <div className={sty["list__no-engineers"]}>
                {appliedTags.length > 0
                  ? "検索条件に一致するエンジニアが見つかりません"
                  : "エンジニアが登録されていません"}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 営業メモ モーダル */}
      {showMemoModal && (
        <div className={sty["memo-modal"]}>
          <div className={sty["memo-modal__header"]}>
            <div className={sty["memo-modal__title"]}>{currentMemo.name}</div>
            <img
              src="/images/close.svg"
              alt="閉じる"
              onClick={handleCloseMemo}
              className={sty["list-icon"]}
              style={{ cursor: "pointer" }}
            />
          </div>
          <div className={sty["memo-modal__body"]}>
            <div>{currentMemo.memo || "メモがありません"}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default EngineerList;
